import React from 'react';
import { fromPairs, map, isEmpty, get, find, filter } from 'lodash';
import classNames from 'classnames';

// local dependencies
import { Form } from 'modules/form';
import { Icon } from 'modules/icon-bank';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { Tooltip } from 'modules/tooltip';
import { textWithPatternMatch } from 'utils/textWithPatternMatch';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { accessManagementUserGroupsListMode as AccessManagementUserGroupsListMode } from 'ui/views/widgets/accessManagementUserGroupsListMode';
 *
 * // render
 * <AccessManagementUserGroupsListMode propName='propValue'/>
 */
export const accessManagementUserGroupsListMode = ( {
    isSearchingUserGroup,
    userGroupSearchResults,
    userGroupSearchQuery,
    userGroups,
    options,
    searchSection,
    userGroupCard,
    onCreateUserGroupActionClick,
    handleListingCardActions,
    onUserGroupSearch
} ) => {
    return (
        <div className='ui-access-management-user-groups-list-mode'>

            {/* User Groups Listing Search */}
            <div className='ui-access-management-user-groups-list-mode__search-section'>
                <Form
                    fields={ fromPairs( map( searchSection.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
                    mode='form'
                    onChangeCallBack={ onUserGroupSearch }
                    onSubmit={ onCreateUserGroupActionClick }
                >
                    { ( { fields, values, onChange, onSumbit } = {} ) => {

                        return (
                            <form
                                onSubmit={ onSumbit }
                                className='ui-access-management-user-groups-list-mode__search-section__form'
                            >
                                { /* fields */ }
                                { map( fields, ( field ) => {

                                    if( isEmpty( userGroups ) ) {
                                        return null;
                                    }

                                    const fieldClassName = classNames( 'ui-access-management-user-groups-list-mode__search-section__form__field', `ui-access-management-user-groups-list-mode__search-section__form__${field.name}` );

                                    return (
                                        <field.view
                                            className={ fieldClassName }
                                            key={ field.name }
                                            name={ field.name }
                                            placeholder={ field.placeholder }
                                            onChange={ onChange }
                                            value={ values[ field.name ] }
                                            showLabel={ false }
                                            prefix={ <Icon id='search' /> }
                                            searchPrefix={ true }
                                            onKeyDown={ ( event ) => { if( 'Enter' === event.key ) { event.preventDefault(); event.stopPropagation(); } } } // stop implicit form submission on Enter key press
                                        />
                                    );
                                } ) }
                                
                                {/* button actions */}
                                <div className='ui-access-management-user-groups-list-mode__search-section__form__actions'>
                                    { map( searchSection.actions, action => {
                                        return (
                                            <Button
                                                key={ action.name }
                                                id= { action.name }
                                                title={ action.title }
                                                type= 'submit'
                                                size='large'
                                                className='ui-access-management-user-groups-list-mode__search-section__form__actions__action'
                                            />
                                        );
                                    } ) }
                                </div>
                            </form>
                        );
                    } }
                </Form>
            </div>

            {/* User Groups Listing */}
            <div className='ui-access-management-user-groups-list-mode__listing'>
                {
                    map( isSearchingUserGroup ? userGroupSearchResults : userGroups, userGroup => {
                        return (
                            <div key={ userGroup.id } className='ui-access-management-user-groups-list-mode__listing__card'>

                                {/* Card Header */}
                                <div className='ui-access-management-user-groups-list-mode__listing__card__header'>

                                    {/* Card Title */}
                                    <div
                                        className='ui-access-management-user-groups-list-mode__listing__card__header__title'
                                        dangerouslySetInnerHTML={ { __html: userGroupSearchQuery ? textWithPatternMatch( {
                                            value: userGroup.name,
                                            pattern: userGroupSearchQuery
                                        } ) : userGroup.name } }
                                    />

                                    {/* Header Actions */}
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__header__actions'>
                                        {
                                            map( userGroupCard.actions, action => {

                                                const actionPayload = {
                                                    ...action,
                                                    ...get( userGroupCard, `${action.name}`, {} ),
                                                    userGroup: userGroup
                                                };

                                                return (
                                                    <div
                                                        key={ action.name }
                                                        onClick = { () => handleListingCardActions( actionPayload ) }
                                                        className='ui-access-management-user-groups-list-mode__listing__card__header__actions__action'
                                                    >
                                                        <Tooltip
                                                            text={ getLabel( action.title ) }
                                                            render={ ( binding = {} ) => (
                                                                <div { ...binding }>
                                                                    <Icon id={ action.name } />
                                                                </div>
                                                            ) }
                                                        />
                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                </div>

                                {/* User Group Info */}
                                <div className='ui-access-management-user-groups-list-mode__listing__card__description'>

                                    {/* Description */}
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__description__description'>
                                        <div className='ui-access-management-user-groups-list-mode__listing__card__description__description__title'>
                                            { getLabel( userGroupCard.description ) }
                                        </div>
                                        <div className='ui-access-management-user-groups-list-mode__listing__card__description__description__text'>
                                            { userGroup.description }
                                        </div>
                                    </div>

                                    {/* Created Date */}
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__description__createdDate'>
                                        <div className='ui-access-management-user-groups-list-mode__listing__card__description__createdDate__title'>
                                        { getLabel( userGroupCard.createdDate ) }
                                        </div>
                                        <div className='ui-access-management-user-groups-list-mode__listing__card__description__createdDate__text'>
                                            { userGroup.createdDate }
                                        </div>
                                    </div>

                                    {/* Created By */}
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__description__createdBy'>
                                        <div className='ui-access-management-user-groups-list-mode__listing__card__description__createdBy__title'>
                                            { getLabel( userGroupCard.createdBy ) }
                                        </div>
                                        <div className='ui-access-management-user-groups-list-mode__listing__card__description__createdBy__text'>
                                            { userGroup.createdBy }
                                        </div>
                                    </div>
                                </div>

                                {/* User Group Permissions */}
                                <div className='ui-access-management-user-groups-list-mode__listing__card__permissions'>
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__permissions__title'>
                                        { getLabel( userGroupCard.permissions.title ) }
                                    </div>
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__permissions__list'>
                                        {/* Active Permissions */}
                                        {
                                            map( filter( options.permissionsOptions, permissionOption => find( userGroup.permissions, { value: permissionOption.value } ) ), permissionOption => {
                                                return (
                                                    <div key={ permissionOption.name } className='ui-access-management-user-groups-list-mode__listing__card__permissions__list__badge ui-access-management-user-groups-list-mode__listing__card__permissions__list__badge--available'>
                                                        <div className='ui-access-management-user-groups-list-mode__listing__card__permissions__list__badge__text'>
                                                            { getLabel( permissionOption.title ) }
                                                        </div>
                                                    </div>
                                                );
                                            } )
                                        }

                                        {/* Inactive Permissions */}
                                        {
                                            map( filter( options.permissionsOptions, permissionOption => ! find( userGroup.permissions, { value: permissionOption.value } ) ), permissionOption => {
                                                return (
                                                    <div key={ permissionOption.name } className='ui-access-management-user-groups-list-mode__listing__card__permissions__list__badge'>
                                                        <div className='ui-access-management-user-groups-list-mode__listing__card__permissions__list__badge__text'>
                                                            { getLabel( permissionOption.title ) }
                                                        </div>
                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                </div>

                                {/* User Group Users */}
                                <div className='ui-access-management-user-groups-list-mode__listing__card__users'>
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__users__title'>
                                        { getLabel( userGroupCard.users.title ) }
                                    </div>

                                    {/* Users List */}
                                    <div className='ui-access-management-user-groups-list-mode__listing__card__users__list'>
                                        {
                                            map( userGroup.users, user => {
                                                return (
                                                    <div key={ user.id } className='ui-access-management-user-groups-list-mode__listing__card__users__list__card'>
                                                        <div className='ui-access-management-user-groups-list-mode__listing__card__users__list__card__name'>
                                                            { user.name }
                                                        </div>
                                                        <div className='ui-access-management-user-groups-list-mode__listing__card__users__list__card__email'>
                                                            { user.email }
                                                        </div>
                                                    </div>
                                                );
                                            } )
                                        }

                                        {/* More Card */}
                                        {
                                            get( userGroup, 'config.moreCard.isVisible', false ) && (
                                                <div className='ui-access-management-user-groups-list-mode__listing__card__users__list__card ui-access-management-user-groups-list-mode__listing__card__users__list__card--more'>
                                                    { getLabel( userGroup.config.moreCard.title ) }
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
accessManagementUserGroupsListMode.displayName = 'accessManagementUserGroupsListMode';

// set default props
accessManagementUserGroupsListMode.defaultProps = {};

// prop types
accessManagementUserGroupsListMode.propTypes = {};
